// Anita Week 1 / Day 4 — Savings Jar and a Goal (U.S. Grade 3 math upgrade).
// Math is Grade 3: weekly totals + average, fraction 1/4 of a goal, division
// "how many weeks", and a two-plan comparison with multiplication. Russian
// stays at RU Grade 1–2.
// SAFETY: paper simulations only. Day 4 keeps parentGuide.financeSafetyNotes
// (required by validator).

const day4 = {
  id: 'day-4',
  studentId: 'anita',
  weekId: 'week-1',
  dayId: 'day-4',
  status: 'active',
  title: 'Копилка и цель / Savings Jar and a Goal',
  shortTitle: 'Копилка и цель / Savings Jar and Goal',
  theme:
    'Маленькие суммы помогают дойти до большой цели. План + терпение = результат. / Small amounts help reach a big goal. Plan + patience = result.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'logic', 'spelling', 'creative', 'speech', 'finance-safety'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни показывает копилку. Сегодня Анита считает, сколько она откладывает за неделю, какое среднее за день, и сколько недель нужно до цели. Она сравнивает два плана накопления. / Nimny shows a savings jar. Today Anita adds up her week, finds the daily average, and figures out how many weeks she needs.',
  goals: [
    'Сложить накопление по дням и найти среднее. / Sum daily savings and find an average.',
    'Считать 1/4 от цели и остаток до цели. / Find 1/4 of a goal and the remainder.',
    'Находить, сколько недель нужно (деление). / Find how many weeks are needed (division).',
    'Сравнивать два плана накопления. / Compare two savings plans.',
    'Объяснить, какой план удобнее в какой ситуации. / Explain which plan fits which situation.'
  ],
  vocabulary: [
    { term: 'копилка / savings jar', definition: 'место, где сохраняют нимы для цели. / a place where nims are saved for a goal.' },
    { term: 'цель / goal', definition: 'то, к чему человек идёт. / what a person is moving toward.' },
    { term: 'неделя / week', definition: 'семь дней. / seven days.' },
    { term: 'осталось / remained', definition: 'сколько нимов ещё нужно. / how many nims are still needed.' },
    { term: 'среднее / average', definition: 'сколько в среднем за день. / the per-day average.' },
    { term: 'план / plan', definition: 'шаги к цели. / steps to a goal.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Слова дня / Words of the Day',
      prompt: 'Прочитай: копилка, цель, неделя, осталось, среднее, план.',
      ruPrimaryText: 'Прочитай по-русски: копилка, цель, неделя, осталось, среднее, план.',
      enHelpText: 'Read in Russian: savings jar, goal, week, remained, average, plan.',
      hint: 'Сначала русское чтение. / Russian first.',
      answer: 'Попытка чтения русских слов. / Attempted Russian reading.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Маленькое чтение / Short Reading',
      prompt:
        'Прочитай по-русски:\n1. Маленькие суммы помогают дойти до большой цели.\n2. Я считаю, сколько отложила за неделю.\n3. Я считаю, сколько недель ещё нужно.\n4. Терпение помогает плану работать.',
      ruPrimaryText:
        'Прочитай 4 короткие фразы про копилку и цель.',
      enHelpText:
        '1. Small amounts help reach a big goal.\n2. I count how much I saved this week.\n3. I count how many weeks are still needed.\n4. Patience helps a plan work.',
      answer: 'Попытка чтения 4 русских фраз. / Attempted Russian reading.'
    },
    {
      id: 'math-1-week-total-and-average',
      type: 'math',
      title: 'Math 1 — Копилка по дням / Savings by Day',
      prompt:
        'Анита откладывала нимы:\nПонедельник — 12\nВторник — 15\nСреда — 18\nЧетверг — 15\n\nСколько всего она отложила?\nСколько в среднем за день?\n\nЗапиши ответ через /: всего / в среднем. Например: 60 / 15.',
      ruPrimaryText:
        '12 + 15 + 18 + 15 = ?. Среднее = сумма ÷ 4. Ответ через /: 60 / 15.',
      enHelpText:
        'Monday 12, Tuesday 15, Wednesday 18, Thursday 15. Find the total. Find the per-day average. Write the answer with a slash: total / average. Example: 60 / 15.',
      helper: 'Сначала сумма, потом сумма ÷ 4. Ответ через /: всего / в среднем. / Sum first, then divide by 4. Format as total / average.',
      answer: '60 / 15',
      unit: 'нимов / nims'
    },
    {
      id: 'math-2-quarter-of-goal',
      type: 'math',
      title: 'Math 2 — До цели / To the Goal',
      prompt:
        'Цель — 100 нимов.\nАнита уже отложила 60.\nПотом она добавила ещё 1/4 цели.\nСколько стало?\nСколько осталось до цели?\n\nЗапиши ответ через /: стало / осталось. Например: 85 / 15.',
      ruPrimaryText:
        'Цель 100. Есть 60. + 1/4 от 100. Сколько стало? Сколько осталось? Ответ через /: 85 / 15.',
      enHelpText:
        'Goal 100 nims. She already has 60. She adds 1/4 of the goal. How many now? How many remain? Write the answer with a slash: now / remain. Example: 85 / 15.',
      helper: '1/4 от 100 = 100 ÷ 4. Ответ через /: стало / осталось. / 1/4 of 100 = 100 ÷ 4. Format as now / remain.',
      answer: '85 / 15',
      unit: 'нимов / nims'
    },
    {
      id: 'math-3-weeks-to-goal',
      type: 'math',
      title: 'Math 3 — Сколько недель нужно / How Many Weeks',
      prompt:
        'Анита откладывает по 12 нимов в неделю.\nЦель — 96 нимов.\nСколько недель нужно?',
      ruPrimaryText:
        '96 ÷ 12 = ?',
      enHelpText:
        'She saves 12 nims per week. The goal is 96 nims. How many weeks are needed?',
      helper: 'Цель ÷ нимы за неделю. / Goal ÷ per-week amount.',
      answer: '8',
      unit: 'недель / weeks'
    },
    {
      id: 'math-4-compare-savings-plans',
      type: 'math',
      title: 'Math 4 — Сравни планы / Compare Plans',
      prompt:
        'План A: откладывать 10 нимов 8 недель.\nПлан B: откладывать 12 нимов 6 недель.\nКакой план даст больше нимов и на сколько?',
      ruPrimaryText:
        'A = 10 × 8. B = 12 × 6. Какой больше и на сколько?',
      enHelpText:
        'Plan A: 10 nims per week for 8 weeks. Plan B: 12 nims per week for 6 weeks. Which gives more, by how many?',
      helper: 'Перемножь и сравни. / Multiply each and compare.',
      answer: 'A больше на 8 / A bigger by 8',
      unit: 'нимов / nims'
    },
    {
      id: 'logic-small-long-vs-big-short',
      type: 'text',
      title: 'Логика — какой план удобнее? / Logic — Which Plan Is Easier?',
      prompt:
        'Что лучше: маленькая сумма долго или большая сумма недолго?\nКогда какой план удобнее?\nОтветь 1–2 предложения.',
      ruPrimaryText:
        'Маленькая сумма долго или большая недолго? Когда какой?',
      enHelpText:
        'What is better: a small amount for a long time, or a big amount for a short time? When does each plan fit? Answer in 1–2 sentences.',
      placeholder: 'Маленькая сумма долго подходит, когда ... Большая сумма недолго подходит, когда ...',
      answer:
        'Принимаются любые осмысленные ответы: «Маленькая — когда нет много нимов сразу.» «Большая — когда хочется быстрее.» «Маленькая — если цель далеко.»'
    },
    {
      id: 'spelling-saving',
      type: 'text',
      title: 'Правописание / Spelling',
      prompt:
        'Напиши:\nкопилка\nцель\nнеделя\nсреднее\n\nВставь буквы:\nкоп_лка\nце_ь\nнеде_я\nср_днее',
      ruPrimaryText: 'Напиши слова и вставь буквы.',
      enHelpText: 'Write the words and fill in the missing letters: копилка, цель, неделя, среднее.',
      answer: 'копилка, цель, неделя, среднее.'
    },
    {
      id: 'creative-saving-scale',
      type: 'text',
      title: 'Моя шкала накопления / My Savings Scale',
      prompt:
        'Нарисуй цель накопления и шкалу 0 – 10 – 20 – 30 – 40 – 50 – 60 – 70 – 80 – 90 – 100.\nЗаполни:\nМоя цель: ______.\nНужно: ______ нимов.\nЕсть: ______ нимов.\nОсталось: ______ нимов.\nОтметь на шкале «Я здесь».',
      ruPrimaryText: 'Нарисуй шкалу 0–100 и отметь «Я здесь» возле своего текущего значения.',
      enHelpText: 'Draw the 0–100 scale and mark "I am here" at your current saving.',
      answer:
        'Open-ended. Проверить, что есть + остаток = «нужно».',
      helper:
        'Подсказка для ребёнка: нимы — это призовые токены курса. Родитель сам решает, какую награду можно получить за накопленные нимы. / Hint: nims are course prize tokens; the parent decides what reward you get.'
    },
    {
      id: 'speech-saving-goal',
      type: 'text',
      title: 'Скажи родителю / Tell Your Parent',
      prompt: 'Скажи по-русски: «Я хочу накопить на ______, и мне нужно ___ недель.»',
      ruPrimaryText: 'Скажи по-русски: «Я хочу накопить на …, и мне нужно … недель».',
      enHelpText: 'Say in Russian: "I want to save for …, and I need … weeks".',
      answer:
        'Принимается любая русская фраза с целью и числом недель.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова дня.',
    '2. Реши Math 1–4 с шагами.',
    '3. Ответь, какой план удобнее и в какой ситуации.',
    '4. Напиши 4 слова на правописание.',
    '5. Нарисуй шкалу 0–100 и отметь «Я здесь».',
    '6. Скажи родителю: «Я хочу накопить на …, и мне нужно … недель».'
  ],
  printWorksheet: {
    title: 'Копилка и цель / Savings Jar and a Goal',
    subtitle: 'Неделя 1 · День 4 / Week 1 · Day 4',
    pages: [
      {
        title: 'Маршрут дня · Слова · Чтение / Route, Words and Reading',
        blocks: [
          { type: 'text', text: 'Миссия: Анита учится считать сумму, среднее, недели до цели. / Mission: Anita learns total, average, and weeks-to-goal.' },
          { type: 'list', items: [
            'Слова дня: копилка, цель, неделя, осталось, среднее, план. / Words of the day.',
            'Маленькое чтение — 4 фразы про копилку и цель (см. экран). / Short Russian reading.',
            'English help мелким шрифтом. / English help in small print.'
          ] }
        ]
      },
      {
        title: 'Математика копилки / Savings Math',
        blocks: [
          { type: 'text', text: 'Показывай шаги. Используй ÷ для среднего и для «сколько недель». / Show steps. Use ÷ for average and for "how many weeks".' },
          { type: 'list', items: [
            'Math 1. 12 + 15 + 18 + 15 = ___ нимов всего. Среднее = ___ ÷ 4 = ___ нимов в день. / Total and average.',
            'Math 2. 1/4 от 100 = 100 ÷ 4 = ___. Стало = 60 + ___ = ___. Осталось до цели = 100 − ___ = ___. / Quarter of a goal.',
            'Math 3. 96 ÷ 12 = ___ недель. / Weeks to goal.',
            'Math 4. A = 10 × 8 = ___; B = 12 × 6 = ___. Где больше и на сколько = ___? / Compare two plans.',
            'Логика: маленькая сумма долго или большая сумма недолго — когда какой план удобнее? / Logic.'
          ] }
        ]
      },
      {
        title: 'Правописание, шкала и parent check / Spelling, Scale and Parent Check',
        blocks: [
          { type: 'list', items: [
            'Правописание: напиши копилка, цель, неделя, среднее. Вставь буквы: коп_лка, це_ь, неде_я, ср_днее. / Spelling.',
            'Моя шкала 0–100 — мини-проект: цель ___, нужно ___, есть ___, осталось ___ . Нарисуй шкалу и отметь «Я здесь». / My 0–100 scale mini-project: draw the scale and mark "I am here".',
            'Скажи родителю: «Я хочу накопить на …, и мне нужно … недель». / Say in Russian to a parent.',
            'Parent Check: сумма, среднее, 1/4 от цели, ÷ для недель, сравнение двух планов. / Parent Check.',
            'Notebook Mode fallback. / Notebook Mode fallback.'
          ] }
        ]
      }
    ]
  },
  parentGuide: {
    goal:
      'Анита считает недельное накопление и среднее, находит 1/4 цели, считает количество недель и сравнивает два плана.',
    mathGoal:
      'U.S. Grade 3: сумма из четырёх слагаемых + деление для среднего, дробь 1/4 как ÷ 4, деление «сколько недель», сравнение двух произведений (× 8 vs × 6).',
    russianGoal:
      'Прочитать слова: копилка, цель, неделя, осталось, среднее, план.',
    readingWritingGoal:
      'Прочитать 4 короткие фразы, написать 4 слова, заполнить шкалу 0–100.',
    practicalGoal:
      'Объяснить, какой план удобнее в какой ситуации.',
    whatThisLessonTeaches:
      'Анита учится планировать накопление: сумма за неделю, среднее за день, доля от цели, число недель и сравнение двух планов.',
    keyMessage:
      'Цель достигается маленькими шагами. Сумма и среднее показывают, как идёт накопление; деление показывает, сколько ещё нужно.',
    howToExplain:
      'Возьмите банку и бумажные нимы. Складывайте по дням, потом считайте сумму и среднее. Покажите, что 1/4 цели — это «один из четырёх равных кусочков».',
    exampleDialogue:
      'Parent: Сколько ты отложила за 4 дня?\nChild: 60.\nParent: Сколько в среднем за день?\nChild: 60 ÷ 4 = 15.\nParent: А сколько недель нужно до цели 96, если откладывать по 12?\nChild: 96 ÷ 12 = 8.',
    readingSupport:
      'Слова «среднее» и «осталось» можно прочитать по слогам с родителем.',
    russianSpeechGoal:
      'Анита говорит: «Я хочу накопить на …, и мне нужно … недель.»',
    mathAnswerKey:
      'Math 1: 12 + 15 + 18 + 15 = 60; 60 ÷ 4 = 15 в среднем.\nMath 2: 1/4 от 100 = 25; 60 + 25 = 85; 100 − 85 = 15.\nMath 3: 96 ÷ 12 = 8 недель.\nMath 4: A = 10 × 8 = 80; B = 12 × 6 = 72; A − B = 8 → A больше на 8.',
    spellingAnswerKey: 'копилка\nцель\nнеделя\nсреднее',
    answerKey: [
      { taskId: 'reading-words', answer: 'Answer: попытка чтения 6 русских слов.\nSolution: Анита читает по очереди.\nParent note: Засчитывать честную попытку.' },
      { taskId: 'reading-sentences', answer: 'Answer: попытка чтения 4 фраз про копилку.\nSolution: Каждая фраза — отдельно.\nParent note: Английский — только для смысла.' },
      { taskId: 'math-1-week-total-and-average', answer: 'Answer: всего 60 нимов; в среднем 15.\nSolution: 12 + 15 + 18 + 15 = 60; 60 ÷ 4 = 15.\nParent note: Связывайте «среднее» с «сколько в среднем за один день».' },
      { taskId: 'math-2-quarter-of-goal', answer: 'Answer: стало 85; осталось 15.\nSolution: 1/4 от 100 = 25; 60 + 25 = 85; 100 − 85 = 15.\nParent note: Подчеркните, что 1/4 — это деление на 4 равные части.' },
      { taskId: 'math-3-weeks-to-goal', answer: 'Answer: 8 недель.\nSolution: 96 ÷ 12 = 8.\nParent note: Это деление «сколько раз 12 умещается в 96».' },
      { taskId: 'math-4-compare-savings-plans', answer: 'Answer: План A больше на 8 нимов.\nSolution: A = 10 × 8 = 80; B = 12 × 6 = 72; 80 − 72 = 8.\nParent note: Подчеркните, что больше недель × меньшая сумма может дать больше, чем меньше недель × большая сумма.' },
      { taskId: 'logic-small-long-vs-big-short', answer: 'Answer: разные планы подходят разным ситуациям.\nSolution: Маленькая сумма долго — когда нельзя сразу много. Большая сумма недолго — когда есть резерв и цель близко.\nParent note: Принимается любое осмысленное объяснение с примером.' },
      { taskId: 'spelling-saving', answer: 'Answer: копилка, цель, неделя, среднее.\nSolution: Вставленные буквы: и, л, л, е.\nParent note: Мягкие ошибки допустимы.' },
      { taskId: 'creative-saving-scale', answer: 'Answer: шкала 0–100 с отметкой «Я здесь».\nSolution: «Есть» + «осталось» = «нужно».\nParent note: Главное — числа на шкале согласованы с задачей.' },
      { taskId: 'speech-saving-goal', answer: 'Answer: «Я хочу накопить на …, и мне нужно … недель.»\nSolution: Любая русская фраза с целью и числом недель.\nParent note: Главное — мысль и понятная цифра.' }
    ],
    check: [
      'Анита прочитала русские слова и 4 фразы.',
      'Анита решила Math 1–4 с показом шагов.',
      'Анита объяснила, какой план удобнее и когда.',
      'Анита нарисовала шкалу 0–100 и поставила «Я здесь».',
      'Анита сказала фразу про цель и недели.'
    ],
    spellingCheck:
      'Проверить буквы в словах копилка, цель, неделя, среднее.',
    readingCheck:
      'Засчитывать попытку чтения. Слово «среднее» можно подсказать.',
    practicalCheck:
      'Анита понимает идею «среднее за день» и «сколько недель до цели».',
    drawingCriteria:
      'Шкала 0–100 нарисована, есть отметка «Я здесь», числа цели и остатка согласованы.',
    financeSafetyNotes:
      'Это бумажная симуляция накопления для обучения, не реальные деньги. Не открывайте ребёнку реальный банковский счёт, не используйте номера карт, CVV или брокерские приложения. Не говорите ребёнку, что нимы можно конвертировать в доллары или иную валюту. Не показывайте торговые платформы. Все решения о реальных подарках принимает родитель.',
    commonMistakes: [
      '«Среднее = последний день.» Правильно: среднее = сумма ÷ число дней.',
      '«1/4 = 4.» Правильно: 1/4 от 100 = 25 (100 ÷ 4).',
      '«Чем больше за неделю — тем больше всего.» Правильно: важна и сумма, и число недель.'
    ],
    discussionPrompts: [
      'Когда удобно копить понемногу?',
      'Когда удобно копить большими шагами?',
      'Почему 1/4 — это деление на 4 равные части?'
    ],
    ifEasy:
      'Усложните: Анита откладывает 5 дней — 8, 10, 12, 14, 16 нимов. Сумма? Среднее? (Ответ: 60; 12.) Сколько недель нужно до 84 при 14 нимах в неделю? (84 ÷ 14 = 6.)',
    ifDifficult:
      'Оставьте только Math 1 и Math 3. Math 1 разберите по дням.',
    rewardGuidance:
      'Базовая награда — 3 нима. Бонус за самостоятельный расчёт среднего или за объяснение выбора плана.',
    parentOnlyNotes:
      'Не превращайте копилку в реальный сберегательный счёт. Не используйте слова «зарплата», «оплата», «кошелёк». Все награды утверждаются родителем отдельно.',
    tokenReward:
      'Нимы — это призовые токены курса, а не оплата.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'average-math', label: 'Бонус за самостоятельный расчёт среднего', amount: 1 },
      { id: 'compare-savings-plans', label: 'Бонус за сравнение двух планов накопления', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'math-1-week-total-and-average',
      'math-2-quarter-of-goal',
      'math-3-weeks-to-goal',
      'math-4-compare-savings-plans',
      'logic-small-long-vs-big-short',
      'spelling-saving',
      'creative-saving-scale',
      'speech-saving-goal'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-1-day-5'
};

export default day4;
